import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarRange, Package, CalendarX2, MessageSquare, Users, Images, FileText, BarChart3, Star, LogOut, Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarRange },
  { to: "/admin/packages", label: "Packages", icon: Package },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarX2 },
  { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
  { to: "/admin/vendors", label: "Vendors", icon: Users },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/invoices", label: "Invoices", icon: FileText },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const SideNav = () => (
    <nav className="flex flex-col gap-1 p-4">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => setOpen(false)}
          data-testid={`admin-nav-${n.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-gold/15 text-gold" : "text-zinc-600 hover:text-zinc-900 hover:bg-pink-50"}`
          }
        >
          <n.icon size={18} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex flex-col w-64 border-r border-pink-200 bg-white shrink-0">
        <div className="h-20 flex items-center px-6 border-b border-pink-200">
          <img src={LOGO_URL} alt="Metrix" className="h-10" />
        </div>
        <SideNav />
        <div className="mt-auto p-4 border-t border-pink-200">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
            <div className="text-xs truncate"><div className="text-zinc-900 truncate">{user?.name}</div><div className="text-zinc-500 truncate">{user?.email}</div></div>
          </div>
          <Button onClick={doLogout} variant="outline" data-testid="admin-logout-btn" className="w-full border-pink-300 text-zinc-700"><LogOut size={15} className="mr-2" /> Logout</Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden h-16 flex items-center justify-between px-5 border-b border-pink-200 bg-white">
          <img src={LOGO_URL} alt="Metrix" className="h-8" />
          <button onClick={() => setOpen((o) => !o)} className="text-zinc-900" data-testid="admin-mobile-toggle">{open ? <X /> : <Menu />}</button>
        </header>
        {open && (
          <div className="lg:hidden border-b border-pink-200 bg-white">
            <SideNav />
            <div className="p-4"><Button onClick={doLogout} variant="outline" className="w-full border-pink-300 text-zinc-700"><LogOut size={15} className="mr-2" /> Logout</Button></div>
          </div>
        )}
        <div className="flex-1 p-5 lg:p-8 overflow-x-hidden">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
